import { type ReactNode, useEffect, useRef, useState } from "react";

interface CarouselProps {
	children: ReactNode;
	className?: string;
	contentClassName?: string;
	showIndicators?: boolean;
}

export function Carousel({
	children,
	className,
	contentClassName,
	showIndicators = true,
}: CarouselProps) {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [showLeftIndicator, setShowLeftIndicator] = useState(false);
	const [showRightIndicator, setShowRightIndicator] = useState(false);

	const checkScroll = () => {
		const el = scrollContainerRef.current;
		if (!el) return;

		setShowLeftIndicator(el.scrollLeft > 0);
		setShowRightIndicator(el.scrollLeft < el.scrollWidth - el.clientWidth);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return;

		checkScroll();
		el.addEventListener("scroll", checkScroll);
		window.addEventListener("resize", checkScroll);

		return () => {
			el.removeEventListener("scroll", checkScroll);
			window.removeEventListener("resize", checkScroll);
		};
	}, []);

	return (
		<div className={`relative flex items-center min-w-0 ${className}`}>
			<div className="flex-1 min-w-0">
				{showIndicators && showLeftIndicator && (
					<div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-100 to-transparent z-(--z-index-carousel-indicator)" />
				)}

				<div
					ref={scrollContainerRef}
					className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
				>
					<div className={`flex gap-2 flex-nowrap ${contentClassName}`}>
						{children}
					</div>
				</div>

				{showIndicators && showRightIndicator && (
					<div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-100 to-transparent z-(--z-index-carousel-indicator)" />
				)}
			</div>
		</div>
	);
}
